/**
 * ContractKanbanColumn - Coluna (grupo de status) do Kanban de Contratos
 *
 * Cada coluna carrega os próprios contratos com cursor-based pagination e
 * infinite scroll, no mesmo padrão do Kanban de Negócios. Não há drag & drop:
 * os cards mudam de coluna automaticamente conforme o status do contrato.
 *
 * @version 1.4.0
 * RELEASE: 2026-09-26 v1.4.0 - Reutiliza a coluna-base compartilhada sem
 *          alterar cabeçalho, paginação, estados ou cartões.
 * RELEASE: 2026-08-12 v1.3.0 - Removida a borda externa da coluna (padrão do Kanban de Negócios):
 *          sem linha de contorno ao redor dos cards, apenas fundo `bg-muted/30`
 * RELEASE: 2026-08-12 v1.2.0 - Header colorido (headerClass) igual ao Kanban de Negócios,
 *          ScrollArea no padrão `flex-1 h-0 min-h-0 p-2` (scroll interno dos cards) e
 *          remoção da faixa lateral colorida do card
 * RELEASE: 2026-08-12 v1.1.0 - ScrollArea com axis="y" (fluidContent) + overflow-hidden na
 *          coluna: conteúdo passa a respeitar os 300px, habilitando truncate nos cards
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import React from 'react';
import { ReadonlyKanbanColumn } from '@/components/kanban/ReadonlyKanbanColumn';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { ContractKanbanCard } from './ContractKanbanCard';
import type { ContractKanbanColumnDef } from './contractKanbanColumns';
import { useContractKanbanColumn } from '@/hooks/useContractKanbanColumn';
import type { ContractFilters } from '@/hooks/useContractsPaginated';
import type { KanbanContract } from '@/hooks/useContractKanbanColumn';

interface ContractKanbanColumnProps {
  /** Definição fixa da coluna */
  column: ContractKanbanColumnDef;
  /** Tenant ativo */
  tenantId: string;
  /** Filtros compartilhados com a listagem */
  filters?: ContractFilters;
  /** Total de contratos da coluna (vem da RPC de contagens) */
  totalCount: number;
  /** Somatório do valor dos negócios da coluna */
  totalValue: number;
  /** Clique em um card */
  onSelectContract: (contract: KanbanContract) => void;
  /** Callback após uma ação de contrato */
  onActionComplete: () => void;
}

export function ContractKanbanColumn({
  column,
  tenantId,
  filters,
  totalCount,
  totalValue,
  onSelectContract,
  onActionComplete,
}: ContractKanbanColumnProps) {
  const {
    contracts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useContractKanbanColumn(column.group, filters);

  return (
    <ReadonlyKanbanColumn
      label={column.label}
      count={totalCount}
      barClass={column.barClass}
      headerClass={column.headerClass}
      itemCount={contracts.length}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      emptyLabel="Nenhum contrato"
      headerDetail={totalValue > 0 ? (
          <PriceDisplay
            amount={totalValue}
            size="sm"
            truncate
            className="mt-0.5 text-[11px] text-muted-foreground"
          />
      ) : undefined}
    >
      {contracts.map((contract) => (
        <ContractKanbanCard
          key={contract.id}
          contract={contract}
          tenantId={tenantId}
          onClick={() => onSelectContract(contract)}
          onActionComplete={onActionComplete}
        />
      ))}
    </ReadonlyKanbanColumn>
  );
}
